import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Link,
  Navigate,
  useLocation,
  type RouteObject,
} from 'react-router-dom';
import { requiredPermissionFor } from '@aurora-pvi/contracts';
import { useSession } from './providers/SessionProvider';
import { GlassShell } from '../app-v2/GlassShell';
import { RouteFallback } from './RouteFallback';
import { RouteError } from './RouteError';

/**
 * Route-level code splitting. The mapping studios and scenario comparison pull
 * in the graph and chart libraries, which are the heaviest dependencies in the
 * bundle; a user who never opens them never downloads them.
 */

// Aurora Glass — the application. The earlier route tree has been retired;
// every legacy address redirects here.
const PortfolioGlass = lazy(() => import('../features-v2/portfolio/PortfolioGlassPage'));
const ProductListGlass = lazy(() => import('../features-v2/products/ProductListGlassPage'));
const Product360Glass = lazy(() => import('../features-v2/products/Product360GlassPage'));
const ExecutiveGlass = lazy(() => import('../features-v2/executive/ExecutiveGlassPage'));
const CandidatesGlass = lazy(() => import('../features-v2/discover/CandidatesGlassPage'));
const WizardGlass = lazy(() => import('../features-v2/discover/WizardGlassPage'));
const ResultsGlass = lazy(() => import('../features-v2/discover/ResultsGlassPage'));
const ProcessMappingGlass = lazy(() => import('../features-v2/mapping/ProcessMappingGlassPage'));
const AppMappingGlass = lazy(() => import('../features-v2/mapping/ApplicationMappingGlassPage'));
const ApplicationsGlass = lazy(() => import('../features-v2/applications/ApplicationsGlassPage'));
const ImportCenterGlass = lazy(() => import('../features-v2/data/ImportCenterGlassPage'));
const ImportWorkbenchGlass = lazy(() => import('../features-v2/data/ImportWorkbenchGlassPage'));
const ImportSessionGlass = lazy(() => import('../features-v2/data/ImportSessionGlassPage'));
const IngestionRunGlass = lazy(() => import('../features-v2/data/IngestionRunGlassPage'));
const ExportsGlass = lazy(() => import('../features-v2/data/ExportsGlassPage'));
const DeliveryGlass = lazy(() => import('../features-v2/delivery/DeliveryGlassPage'));
const FlowGlass = lazy(() => import('../features-v2/delivery/FlowGlassPage'));
const InvestmentGlass = lazy(() => import('../features-v2/value/InvestmentGlassPage'));
const WorkforceGlass = lazy(() => import('../features-v2/value/WorkforceGlassPage'));
const SuppliersGlass = lazy(() => import('../features-v2/value/SuppliersGlassPage'));
const ContractsGlass = lazy(() => import('../features-v2/value/ContractsGlassPage'));
const ScenarioListGlass = lazy(() => import('../features-v2/scenarios/ScenarioListGlassPage'));
const ScenarioCompareGlass = lazy(
  () => import('../features-v2/scenarios/ScenarioCompareGlassPage'),
);
const IntegrationAdminGlass = lazy(() => import('../features-v2/admin/IntegrationAdminGlassPage'));
const LineageGlass = lazy(() => import('../features-v2/admin/LineageGlassPage'));
const CommandCenterGlass = lazy(() => import('../features-v2/admin/CommandCenterGlassPage'));
const ApprovalsGlass = lazy(() => import('../features-v2/admin/ApprovalsGlassPage'));
const CustomFieldsGlass = lazy(() => import('../features-v2/admin/CustomFieldsGlassPage'));
const HeroLanding = lazy(() => import('../features-v2/landing/HeroLandingPage'));
const CalculationsGlass = lazy(() => import('../features-v2/reports/CalculationsGlassPage'));
const TeamsGlass = lazy(() => import('../features-v2/value/TeamsGlassPage'));
const Landing = lazy(() => import('../features-v2/landing/LandingPage'));

// Identity, access and span of control (backlog D02).
const SignInGlass = lazy(() => import('../features-v2/identity/SignInGlassPage'));
const UserProfileGlass = lazy(() => import('../features-v2/identity/UserProfileGlassPage'));
const RolesPermissionsGlass = lazy(
  () => import('../features-v2/identity/RolesPermissionsGlassPage'),
);
const DelegationGlass = lazy(() => import('../features-v2/identity/DelegationGlassPage'));

// Outcome traceability — work item to release to outcome (backlog UC-09-066).
const OutcomeTraceabilityGlass = lazy(
  () => import('../features-v2/outcomes/OutcomeTraceabilityGlassPage'),
);

// Collaboration, reporting and taxonomy governance (backlog D13).
const CollaborationGlass = lazy(
  () => import('../features-v2/collaboration/CollaborationGlassPage'),
);
const WatchlistGlass = lazy(() => import('../features-v2/collaboration/WatchlistGlassPage'));
const ReportsGlass = lazy(() => import('../features-v2/reports/ReportsGlassPage'));
const TaxonomyGovernanceGlass = lazy(
  () => import('../features-v2/reports/TaxonomyGovernanceGlassPage'),
);

// Estate analytics — duplicates, modernisation disposition, period comparison
// (backlog D07 and D03).
const DuplicatesGlass = lazy(() => import('../features-v2/estate/DuplicatesGlassPage'));
const TechnologyHeatmapGlass = lazy(
  () => import('../features-v2/estate/TechnologyHeatmapGlassPage'),
);
const PortfolioCompareGlass = lazy(() => import('../features-v2/estate/PortfolioCompareGlassPage'));

// Process catalog and detail (backlog D06).
const ProcessCatalogGlass = lazy(() => import('../features-v2/process/ProcessCatalogGlassPage'));
const ProcessDetailGlass = lazy(() => import('../features-v2/process/ProcessDetailGlassPage'));

// The one screen outside the shell and outside the identity: a read-only
// portfolio review, opened by a link, by somebody with no account.
const SharedView = lazy(() => import('../features-v2/share/SharedViewPage'));
const RiskRegisterGlass = lazy(() => import('../features-v2/risks/RiskRegisterGlassPage'));
const CapabilitiesGlass = lazy(() => import('../features-v2/capabilities/CapabilitiesGlassPage'));
const AgentEstateGlass = lazy(() => import('../features-v2/agents/AgentEstateGlassPage'));
const TechnologyGlass = lazy(() => import('../features-v2/technology/TechnologyGlassPage'));
const ObjectivesGlass = lazy(() => import('../features-v2/objectives/ObjectivesGlassPage'));
const RoadmapGlass = lazy(() => import('../features-v2/roadmap/RoadmapGlassPage'));
const ReleaseCalendarGlass = lazy(
  () => import('../features-v2/releases/ReleaseCalendarGlassPage'),
);
const FundingGlass = lazy(() => import('../features-v2/planning/FundingGlassPage'));
const CapacityGlass = lazy(() => import('../features-v2/planning/CapacityGlassPage'));

function page(element: React.ReactNode) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <RequirePermission>{element}</RequirePermission>
    </Suspense>
  );
}

/**
 * Refuses a route the caller's permissions do not reach.
 *
 * The navigation already hides these, but hiding a menu item is presentation:
 * a pasted link, a bookmark or a back button all arrive here without passing a
 * rail. This is the second of three independent refusals — the API is the
 * third and the only one that actually protects the data, since a browser can
 * be told anything.
 *
 * It says which permission is missing rather than showing a bare 404. A person
 * who has been sent a link by a colleague needs to know to ask for access, not
 * to conclude the page is broken.
 */
function RequirePermission({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { permissions, permissionsLoaded, canOpen } = useSession();

  if (!permissionsLoaded || canOpen(pathname)) return <>{children}</>;

  const rule = requiredPermissionFor(pathname);
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-[440px] text-center">
        <p className="text-[10px] uppercase tracking-[0.09em] text-[var(--g-text-faint)]">
          Not available to your role
        </p>
        <h1 className="mt-2 text-[19px] font-medium text-[var(--g-text)]">
          You do not have access to this page
        </h1>
        <p className="mt-2 text-[12.5px] leading-relaxed text-[var(--g-text-secondary)]">
          {rule?.reason ??
            'This page is limited to roles that can view the portfolio.'}{' '}
          Your role holds {permissions.length} permission
          {permissions.length === 1 ? '' : 's'} and not{' '}
          <span className="font-mono text-[11.5px]">{rule?.requires ?? 'view'}</span>.
        </p>
        <Link
          to="/v2/portfolio"
          className="mt-4 inline-block text-[12.5px] text-[var(--g-accent)] underline"
        >
          Back to the portfolio
        </Link>
      </div>
    </div>
  );
}

/**
 * Sends a legacy address to its Aurora Glass equivalent.
 *
 * The two trees share their sub-paths, so prefixing is enough for all but one
 * case: the classic portfolio screen lived at `/portfolio/hierarchy` while
 * Glass serves it at `/portfolio`. Query strings are preserved because filter
 * state travels in them, and dropping it would silently widen the scope a
 * reader had narrowed.
 */
function LegacyRedirect() {
  const { pathname, search, hash } = useLocation();
  const mapped = pathname === '/portfolio/hierarchy' ? '/portfolio' : pathname;
  return <Navigate to={`/v2${mapped}${search}${hash}`} replace />;
}

const routes: RouteObject[] = [
  // The front door, and the only route that sits outside both shells — it
  // brings its own chrome and its own theme root. Declared before '/' so the
  // redirect below has somewhere to land.
  {
    path: '/welcome',
    element: page(<Landing />),
    errorElement: <RouteError />,
  },
  // Everything that is not the landing page or an explicit /v2 path is a
  // legacy address. They redirect rather than 404 because links to them exist
  // in bookmarks, in the metric drilldowns inside `ROUTES`, and in screenshots
  // — and a dead link is a worse answer than the page the reader wanted.
  // The landing page is the front door, outside the app shell: no rail, no top
  // bar, no sign-in — one screen and one way in.
  { path: '/', element: page(<HeroLanding />), errorElement: <RouteError /> },

  /*
    Outside `/v2`, and outside `page()`.

    `page()` wraps its element in `RequirePermission`, which asks the session
    what the signed-in person may open — and the whole point of this route is
    that there is no signed-in person. It is also outside `GlassShell`, so the
    recipient gets no navigation rail offering them doors that are locked.

    The server decides everything: the token resolves to a frozen scope and a
    frozen entitlement, and the page renders whatever it is handed.
  */
  {
    path: '/share/:token',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <SharedView />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  { path: '*', element: <LegacyRedirect />, errorElement: <RouteError /> },

  {
    path: '/v2',
    element: <GlassShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/v2/portfolio" replace /> },
      { path: 'executive', element: page(<ExecutiveGlass />) },
      { path: 'portfolio', element: page(<PortfolioGlass />) },

      { path: 'discover/candidates', element: page(<CandidatesGlass />) },
      { path: 'discover/wizard/:candidateId', element: page(<WizardGlass />) },
      { path: 'discover/results/:assessmentId', element: page(<ResultsGlass />) },

      { path: 'products', element: page(<ProductListGlass />) },
      { path: 'products/:productId/:tab?', element: page(<Product360Glass />) },

      { path: 'mapping/process-product', element: page(<ProcessMappingGlass />) },
      { path: 'mapping/application-product', element: page(<AppMappingGlass />) },
      { path: 'applications', element: page(<ApplicationsGlass />) },
      { path: 'applications/:applicationId', element: page(<ApplicationsGlass />) },

      { path: 'data/import', element: page(<ImportWorkbenchGlass />) },
      { path: 'data/import/wizard', element: page(<ImportCenterGlass />) },
      { path: 'data/import/:sessionId', element: page(<ImportSessionGlass />) },
      { path: 'data/runs/:runId', element: page(<IngestionRunGlass />) },
      { path: 'data/exports', element: page(<ExportsGlass />) },

      { path: 'delivery', element: page(<DeliveryGlass />) },
      // Flow is its own screen, not a tab on Delivery: the DORA figures there
      // answer a different question and mixing them invites averaging.
      { path: 'delivery/flow', element: page(<FlowGlass />) },
      { path: 'investment', element: page(<InvestmentGlass />) },
      { path: 'workforce', element: page(<WorkforceGlass />) },
      { path: 'suppliers', element: page(<SuppliersGlass />) },
      { path: 'contracts', element: page(<ContractsGlass />) },

      { path: 'scenarios', element: page(<ScenarioListGlass />) },
      { path: 'scenarios/:scenarioId/compare', element: page(<ScenarioCompareGlass />) },

      { path: 'admin/integrations', element: page(<IntegrationAdminGlass />) },
      { path: 'admin/lineage/:datasetId?', element: page(<LineageGlass />) },
      { path: 'admin/command-center', element: page(<CommandCenterGlass />) },
      { path: 'admin/approvals', element: page(<ApprovalsGlass />) },
      { path: 'admin/fields', element: page(<CustomFieldsGlass />) },
      { path: 'calculations', element: page(<CalculationsGlass />) },
      { path: 'teams', element: page(<TeamsGlass />) },

      { path: 'delivery/outcomes', element: page(<OutcomeTraceabilityGlass />) },

      { path: 'collaboration', element: page(<CollaborationGlass />) },
      { path: 'watchlist', element: page(<WatchlistGlass />) },
      { path: 'reports', element: page(<ReportsGlass />) },
      { path: 'admin/taxonomy', element: page(<TaxonomyGovernanceGlass />) },

      { path: 'applications/duplicates', element: page(<DuplicatesGlass />) },
      { path: 'applications/heatmap', element: page(<TechnologyHeatmapGlass />) },
      { path: 'portfolio/compare', element: page(<PortfolioCompareGlass />) },
      { path: 'risks', element: page(<RiskRegisterGlass />) },
      { path: 'capabilities', element: page(<CapabilitiesGlass />) },
      { path: 'agents', element: page(<AgentEstateGlass />) },
      { path: 'technology', element: page(<TechnologyGlass />) },

      // Strategy sits above the portfolio rather than inside it, so both are
      // top-level paths: an objective cascade is not a view of the estate and
      // a roadmap is not a view of delivery.
      { path: 'objectives', element: page(<ObjectivesGlass />) },
      { path: 'roadmap', element: page(<RoadmapGlass />) },

      // The release calendar is beside the roadmap and not inside delivery. A
      // roadmap is what the portfolio has committed to; a calendar is what is
      // landing and whether it landed when it said it would, which is a
      // question about the plan rather than about the flow of work.
      { path: 'releases', element: page(<ReleaseCalendarGlass />) },

      // Funding sits beside Investment rather than inside it: one reports what
      // was spent, the other holds what was agreed, and merging them would let
      // a proposal be read as an actual.
      { path: 'funding', element: page(<FundingGlass />) },
      { path: 'capacity', element: page(<CapacityGlass />) },

      { path: 'process', element: page(<ProcessCatalogGlass />) },
      { path: 'process/:processId', element: page(<ProcessDetailGlass />) },

      { path: 'signin', element: page(<SignInGlass />) },
      { path: 'profile', element: page(<UserProfileGlass />) },
      { path: 'admin/roles', element: page(<RolesPermissionsGlass />) },
      { path: 'admin/delegation', element: page(<DelegationGlass />) },

      { path: '*', element: <RouteError notFound /> },
    ],
  },
];

/**
 * The annotation is explicit because pnpm's non-hoisted layout puts the router's
 * own types outside this package's reachable tree, so the inferred type has no
 * nameable path. Naming it here keeps the declaration portable.
 */
export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routes);
